import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Archive, ChevronDown, ChevronUp, Download, X } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  type TonPastille,
} from '../components/ui/Primitives';
import { BarreFiltres } from '../components/ui/BarreFiltres';
import { ChampDate } from '../components/ui/ChampDate';
import { ChampFiltre, ChampRecherche } from '../components/ui/Onglets';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { useValeurDifferee } from '../lib/useRecherche';
import {
  exporterJournal,
  periodeIncomplete,
  SANS_COMPTE,
  useJournalAudit,
  type ChoixPeriodeJournal,
  type FamilleAction,
  type FiltresJournal,
  type FormatExportJournal,
  type LigneJournal,
  type VueJournal,
} from '../lib/useJournalAudit';

type Filtres = Omit<FiltresJournal, 'page' | 'taille'>;

const SANS_FILTRE: Filtres = {
  periode: 'TRENTE_JOURS',
  du: '',
  au: '',
  utilisateur: '',
  famille: '',
  action: '',
  dossier: '',
  recherche: '',
};

const TONS_FAMILLE: Record<FamilleAction, TonPastille> = {
  CONNEXION: 'info',
  CONSULTATION: 'neutral',
  EXPORT: 'warning',
  SAISIE: 'mint',
  DOCUMENT: 'info',
  DECISION: 'success',
  FACTURATION: 'mint',
  REGLAGE: 'warning',
  SYSTEME: 'neutral',
};

function tonDeLaLigne(ligne: LigneJournal): TonPastille {
  return ligne.action === 'CONNEXION_REFUSEE' ? 'danger' : TONS_FAMILLE[ligne.famille];
}

function horodatage(instant: string): { jour: string; heure: string } {
  const date = new Date(instant);
  return {
    jour: date.toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' }),
    heure: date.toLocaleTimeString('fr-FR', { timeZone: 'Indian/Reunion' }),
  };
}

function jourFrancais(jour: string): string {
  const [annee, mois, j] = jour.split('-');
  return `${j}/${mois}/${annee}`;
}

function nombre(valeur: number): string {
  return valeur.toLocaleString('fr-FR');
}

function pluriel(valeur: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre(valeur)} ${valeur > 1 ? plurielTexte : singulier}`;
}

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

const CLASSE_TH = 'px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray';
const CLASSE_TD = 'px-4 py-3 align-top';
const CLASSE_LIEN = 'font-semibold text-codialis-sky hover:underline';

/**
 * Journal d'audit (phase 5, TF-438) : qui a fait quoi, quand.
 *
 * L'écran ne compte rien : libellés, familles, objets relus, compteurs et
 * conservation viennent de `/api/journal-audit`, qui lit `JournalAcces`. Il ne
 * modifie aucune trace — aucune route ne le permet. L'export sort la liste
 * filtrée, toutes pages comprises, et se trace lui-même.
 */
export function DirJournalAuditPage(): JSX.Element {
  const [adresse, setAdresse] = useSearchParams();
  const [filtres, setFiltres] = useState<Filtres>(() => ({
    ...SANS_FILTRE,
    dossier: adresse.get('dossier') ?? '',
    utilisateur: adresse.get('utilisateur') ?? '',
  }));
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const [deplie, setDeplie] = useState<string | null>(null);
  const [exportOuvert, setExportOuvert] = useState(false);
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const requete: FiltresJournal = { ...filtres, recherche, page, taille: taillePage };
  const { data, isLoading, error } = useJournalAudit(requete);

  useEffect(
    () => setPage(1),
    [taillePage, recherche, filtres.periode, filtres.du, filtres.au, filtres.utilisateur, filtres.famille, filtres.action, filtres.dossier],
  );

  // Le dossier et le compte filtrés restent dans l'adresse : un lien les rouvre.
  useEffect(() => {
    const suivante = new URLSearchParams(adresse);
    for (const cle of ['dossier', 'utilisateur'] as const) {
      if (filtres[cle]) suivante.set(cle, filtres[cle]);
      else suivante.delete(cle);
    }
    if (suivante.toString() !== adresse.toString()) setAdresse(suivante, { replace: true });
  }, [filtres.dossier, filtres.utilisateur]); // eslint-disable-line react-hooks/exhaustive-deps

  const changer = <C extends keyof Filtres>(cle: C, valeur: Filtres[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));
  const filtreActif = JSON.stringify(filtres) !== JSON.stringify(SANS_FILTRE);
  const actions = (data?.options.actions ?? []).filter((option) => !filtres.famille || option.famille === filtres.famille);
  const compteurs = data?.compteurs;
  const familleDe = (code: FamilleAction): number =>
    compteurs?.parFamille.find((famille) => famille.famille === code)?.traces ?? 0;
  const lignes = data?.lignes ?? [];
  const periodeInvalide = filtres.periode === 'LIBRE' && Boolean(filtres.du && filtres.au && filtres.du > filtres.au);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Journal d’audit"
        sousTitre="Qui a fait quoi, quand · consultations, saisies, décisions, exports et connexions"
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="Traces"
          valeur={compteurs ? nombre(compteurs.traces) : '—'}
          detail={data ? `Sur ${data.periode.libelle}` : 'Sur la période'}
        />
        <Compteur
          libelle="Comptes"
          valeur={compteurs ? nombre(compteurs.auteurs) : '—'}
          ton="blue"
          detail={
            compteurs && compteurs.sansCompte > 0
              ? `+ ${pluriel(compteurs.sansCompte, 'trace')} sans compte connecté`
              : 'Comptes à l’origine d’une trace'
          }
        />
        <Compteur
          libelle="Consultations et exports"
          valeur={compteurs ? nombre(familleDe('CONSULTATION') + familleDe('EXPORT')) : '—'}
          ton="mint"
          detail={compteurs ? `dont ${pluriel(familleDe('EXPORT'), 'téléchargement ou export', 'téléchargements ou exports')}` : 'Dossiers, pièces, documents'}
        />
        <Compteur
          libelle="Connexions refusées"
          valeur={compteurs ? nombre(compteurs.connexionsRefusees) : '—'}
          ton={compteurs && compteurs.connexionsRefusees > 0 ? 'red' : 'navy'}
          detail="Mot de passe incorrect ou compte désactivé"
        />
      </div>

      {data && <EncartConservation conservation={data.conservation} />}

      <BarreFiltres
        onReinitialiser={filtreActif ? () => setFiltres(SANS_FILTRE) : null}
        onExporter={() => setExportOuvert(true)}
        libelleExport="Exporter"
      >
        <ChampRecherche
          valeur={filtres.recherche}
          onChange={(valeur) => changer('recherche', valeur)}
          etiquette="Rechercher dans le journal"
          placeholder="Dossier, compte, objet, adresse IP…"
        />
        <ChampFiltre
          valeur={filtres.periode}
          onChange={(valeur) => {
            const periode = valeur as ChoixPeriodeJournal;
            // Des dates libres partent de la période affichée.
            setFiltres((avant) => ({
              ...avant,
              periode,
              du: periode === 'LIBRE' && !avant.du ? (data?.periode.du ?? '') : avant.du,
              au: periode === 'LIBRE' && !avant.au ? (data?.periode.au ?? '') : avant.au,
            }));
          }}
          etiquette="Période"
        >
          {(data?.options.periodes ?? [{ code: 'TRENTE_JOURS' as ChoixPeriodeJournal, libelle: '30 derniers jours' }]).map(
            (option) => (
              <option key={option.code} value={option.code}>
                {option.libelle}
              </option>
            ),
          )}
        </ChampFiltre>
        {filtres.periode === 'LIBRE' && (
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <ChampDate libelle="Du" valeur={filtres.du} max={filtres.au || undefined} onChange={(du) => changer('du', du)} />
            <ChampDate libelle="au" valeur={filtres.au} min={filtres.du || undefined} onChange={(au) => changer('au', au)} />
          </div>
        )}
        <ChampFiltre valeur={filtres.utilisateur} onChange={(valeur) => changer('utilisateur', valeur)} etiquette="Compte">
          <option value="">Tous les comptes</option>
          {data?.options.utilisateurs.map((compte) => (
            <option key={compte.id} value={compte.id}>
              {compte.nom} · {compte.profil}
              {compte.actif ? '' : ' (inactif)'}
            </option>
          ))}
          <option value={SANS_COMPTE}>Sans compte connecté</option>
        </ChampFiltre>
        <ChampFiltre
          valeur={filtres.famille}
          onChange={(valeur) => setFiltres((avant) => ({ ...avant, famille: valeur, action: '' }))}
          etiquette="Famille d’actions"
        >
          <option value="">Toutes les familles</option>
          {data?.options.familles.map((famille) => (
            <option key={famille.code} value={famille.code}>
              {famille.libelle} ({nombre(famille.traces)})
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre valeur={filtres.action} onChange={(valeur) => changer('action', valeur)} etiquette="Action">
          <option value="">Toutes les actions</option>
          {actions.map((action) => (
            <option key={action.code} value={action.code}>
              {action.libelle} ({nombre(action.traces)})
            </option>
          ))}
        </ChampFiltre>
      </BarreFiltres>

      {filtres.dossier && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex min-h-[36px] max-w-full items-center gap-2 rounded-full bg-codialis-sand px-3 text-xs font-semibold text-codialis-navy">
            <span className="min-w-0 truncate">Dossier : {data?.dossier?.nom ?? (data ? 'dossier supprimé ou introuvable' : '…')}</span>
            <button
              type="button"
              onClick={() => changer('dossier', '')}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full hover:bg-white"
              aria-label="Retirer le filtre sur le dossier"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </span>
        </div>
      )}

      {periodeInvalide && <MessageErreur message="Le premier jour de la période doit précéder le dernier." />}
      {isLoading && <Chargement libelle="Chargement du journal…" />}
      {error && <MessageErreur message={messageErreur(error, 'Le journal n’a pas pu être chargé.')} />}
      {data && !error && lignes.length === 0 && (
        <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">
          {filtreActif && compteurs && compteurs.traces > 0
            ? 'Aucune trace ne correspond aux filtres.'
            : `Aucune trace sur ${data.periode.libelle}.`}
        </p>
      )}

      {data && !error && lignes.length > 0 && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-card">
          {/* Sous l'ordinateur, une carte par trace : le tableau y défilerait de côté. */}
          <ul className="divide-y divide-gray-100 xl:hidden">
            {lignes.map((ligne) => (
              <li key={ligne.id} className="space-y-2 p-4">
                <ResumeLigne ligne={ligne} />
                <BoutonDetail ouvert={deplie === ligne.id} onBasculer={() => setDeplie(deplie === ligne.id ? null : ligne.id)} />
                {deplie === ligne.id && (
                  <DetailLigne ligne={ligne} onFiltrerDossier={(id) => changer('dossier', id)} onFiltrerCompte={(id) => changer('utilisateur', id)} />
                )}
              </li>
            ))}
          </ul>
          <div className="relative hidden overflow-x-auto xl:block">
            <table className="w-full min-w-[920px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Date', 'Auteur', 'Action', 'Objet', 'Dossier'].map((colonne) => (
                    <th key={colonne} className={CLASSE_TH}>
                      {colonne}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lignes.map((ligne) => {
                  const { jour, heure } = horodatage(ligne.survenuLe);
                  return (
                    <LigneTableau
                      key={ligne.id}
                      ligne={ligne}
                      jour={jour}
                      heure={heure}
                      ouvert={deplie === ligne.id}
                      onBasculer={() => setDeplie(deplie === ligne.id ? null : ligne.id)}
                      onFiltrerDossier={(id) => changer('dossier', id)}
                      onFiltrerCompte={(id) => changer('utilisateur', id)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.pagination.page}
            pages={data.pagination.pages}
            total={data.pagination.total}
            affiches={lignes.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="trace"
          />
        </div>
      )}

      {data && (
        <p className="text-[11px] text-codialis-gray">
          Heures de La Réunion · une écriture sans trace métier est tracée par son module et son adresse, sans les
          données saisies · calculé le{' '}
          {new Date(data.calculeLe).toLocaleString('fr-FR', { timeZone: 'Indian/Reunion', dateStyle: 'short', timeStyle: 'short' })}
        </p>
      )}

      {exportOuvert && data && (
        <ModaleExport
          vue={data}
          filtres={{ ...filtres, recherche: filtres.recherche.trim(), page: 1, taille: taillePage }}
          bloque={periodeIncomplete(filtres)}
          onFermer={() => setExportOuvert(false)}
        />
      )}
    </div>
  );
}

function EncartConservation({ conservation }: { conservation: VueJournal['conservation'] }): JSX.Element {
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
      <Archive className="mt-0.5 h-5 w-5 shrink-0 text-codialis-navy" aria-hidden />
      <p className="min-w-0">
        <span className="font-semibold">Conservation : {conservation.ans} ans.</span> Chaque trace est gardée{' '}
        {conservation.ans} ans, puis effacée au passage quotidien de 6 h ; aucune ne se modifie ni ne se supprime avant.{' '}
        {conservation.plusAncienne && conservation.plusAncienneConserveeJusquau ? (
          <>
            Plus ancienne trace : {horodatage(conservation.plusAncienne).jour}, gardée jusqu’au{' '}
            {jourFrancais(conservation.plusAncienneConserveeJusquau)}.
          </>
        ) : (
          'Le journal est vide.'
        )}
      </p>
    </div>
  );
}

function Auteur({ ligne, onFiltrerCompte }: { ligne: LigneJournal; onFiltrerCompte?: (id: string) => void }): JSX.Element {
  if (!ligne.auteur) {
    return (
      <span className="text-codialis-gray" title="Accès public par lien, ou automatisme de la plateforme">
        Sans compte connecté
      </span>
    );
  }
  return (
    <span className="min-w-0">
      {onFiltrerCompte ? (
        <button
          type="button"
          onClick={() => onFiltrerCompte(ligne.auteur!.id)}
          className="text-left font-semibold text-codialis-navy hover:underline"
          title="Voir les traces de ce compte"
        >
          {ligne.auteur.nom}
        </button>
      ) : (
        <span className="font-semibold text-codialis-navy">{ligne.auteur.nom}</span>
      )}
      <span className="block text-[11px] text-codialis-gray">
        {ligne.auteur.profil}
        {ligne.auteur.actif ? '' : ' · compte inactif'}
      </span>
    </span>
  );
}

function Objet({ ligne }: { ligne: LigneJournal }): JSX.Element {
  const { objet } = ligne;
  const lu = objet.libelle ?? ligne.detail.objet;
  // Une écriture générique n'a que son module : il n'est pas répété en nom.
  const nom = lu && lu !== objet.type ? lu : null;
  if (!objet.type && !nom) return <span className="text-codialis-gray">—</span>;
  return (
    <span className="min-w-0 break-words">
      {objet.type && <span className="block text-[11px] text-codialis-gray">{objet.type}</span>}
      {nom &&
        (objet.lien ? (
          <Link to={objet.lien} className={CLASSE_LIEN}>
            {nom}
          </Link>
        ) : (
          <span className="text-codialis-navy">{nom}</span>
        ))}
    </span>
  );
}

function Dossier({ ligne }: { ligne: LigneJournal }): JSX.Element {
  if (!ligne.dossier) return <span className="text-codialis-gray">—</span>;
  return (
    <Link to={ligne.dossier.lien} className={CLASSE_LIEN}>
      {ligne.dossier.nom}
    </Link>
  );
}

function ResumeLigne({ ligne }: { ligne: LigneJournal }): JSX.Element {
  const { jour, heure } = horodatage(ligne.survenuLe);
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Pastille texte={ligne.familleLibelle} ton={tonDeLaLigne(ligne)} />
        <span className="min-w-0 break-words text-sm font-semibold text-codialis-navy">{ligne.actionLibelle}</span>
      </div>
      <div className="text-xs text-codialis-gray">
        {jour} à {heure}
      </div>
      <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Auteur</div>
          <Auteur ligne={ligne} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Objet</div>
          <Objet ligne={ligne} />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Dossier</div>
          <Dossier ligne={ligne} />
        </div>
      </div>
    </div>
  );
}

function BoutonDetail({ ouvert, onBasculer }: { ouvert: boolean; onBasculer: () => void }): JSX.Element {
  const Icone = ouvert ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={onBasculer}
      aria-expanded={ouvert}
      className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:underline"
    >
      <Icone className="h-4 w-4" aria-hidden />
      {ouvert ? 'Masquer le détail' : 'Détail'}
    </button>
  );
}

function LigneTableau({
  ligne,
  jour,
  heure,
  ouvert,
  onBasculer,
  onFiltrerDossier,
  onFiltrerCompte,
}: {
  ligne: LigneJournal;
  jour: string;
  heure: string;
  ouvert: boolean;
  onBasculer: () => void;
  onFiltrerDossier: (id: string) => void;
  onFiltrerCompte: (id: string) => void;
}): JSX.Element {
  return (
    <>
      <tr className="transition hover:bg-gray-50">
        <td className={`${CLASSE_TD} whitespace-nowrap`}>
          <div className="font-semibold text-codialis-navy">{jour}</div>
          <div className="text-[11px] text-codialis-gray">{heure}</div>
          <div className="-ml-2 mt-1">
            <BoutonDetail ouvert={ouvert} onBasculer={onBasculer} />
          </div>
        </td>
        <td className={`${CLASSE_TD} max-w-[220px]`}>
          <Auteur ligne={ligne} onFiltrerCompte={onFiltrerCompte} />
        </td>
        <td className={`${CLASSE_TD} max-w-[300px]`}>
          <div className="space-y-1">
            <Pastille texte={ligne.familleLibelle} ton={tonDeLaLigne(ligne)} />
            <div className="break-words font-semibold text-codialis-navy">{ligne.actionLibelle}</div>
          </div>
        </td>
        <td className={`${CLASSE_TD} max-w-[260px]`}>
          <Objet ligne={ligne} />
        </td>
        <td className={`${CLASSE_TD} max-w-[200px]`}>
          <Dossier ligne={ligne} />
        </td>
      </tr>
      {ouvert && (
        <tr className="bg-gray-50/60">
          <td colSpan={5} className="px-4 pb-4 pt-1">
            <DetailLigne ligne={ligne} onFiltrerDossier={onFiltrerDossier} onFiltrerCompte={onFiltrerCompte} />
          </td>
        </tr>
      )}
    </>
  );
}

function DetailLigne({
  ligne,
  onFiltrerDossier,
  onFiltrerCompte,
}: {
  ligne: LigneJournal;
  onFiltrerDossier: (id: string) => void;
  onFiltrerCompte: (id: string) => void;
}): JSX.Element {
  const { detail } = ligne;
  const elements: [string, string | null][] = [
    ['Code de l’action', ligne.action],
    ['Identifiant de l’objet', ligne.objet.identifiant],
    ['Adresse IP', ligne.adresseIp],
    ['Appareil', ligne.appareil],
    ['E-mail de l’auteur', ligne.auteur?.email ?? null],
  ];
  return (
    <div className="space-y-3 rounded-xl border border-gray-100 bg-white p-3 text-xs sm:p-4">
      {detail.objet && detail.objet !== ligne.objet.libelle && (
        <p className="break-words font-semibold text-codialis-navy">{detail.objet}</p>
      )}
      {detail.changements.length > 0 && (
        <ul className="space-y-0.5 text-codialis-navy">
          {detail.changements.map((changement, index) => (
            <li key={`${changement.champ}-${index}`} className="break-words">
              <span className="font-semibold">{changement.champ}</span> :{' '}
              <span className="text-codialis-gray line-through">{changement.avant}</span> → {changement.apres}
            </li>
          ))}
        </ul>
      )}
      {detail.route && (
        <p className="break-all font-mono text-[11px] text-codialis-gray">{detail.route}</p>
      )}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
        {elements.map(([libelle, valeur]) => (
          <div key={libelle} className="min-w-0">
            <dt className="text-[11px] font-bold uppercase tracking-wider text-codialis-gray">{libelle}</dt>
            <dd className="break-all text-codialis-navy">{valeur ?? '—'}</dd>
          </div>
        ))}
      </dl>
      {ligne.agent && ligne.appareil !== ligne.agent && (
        <p className="break-all text-[11px] text-codialis-gray" title="En-tête User-Agent du navigateur">
          {ligne.agent}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {ligne.dossier && (
          <BoutonSecondaire onClick={() => onFiltrerDossier(ligne.dossier!.id)}>Voir les traces de ce dossier</BoutonSecondaire>
        )}
        {ligne.auteur && (
          <BoutonSecondaire onClick={() => onFiltrerCompte(ligne.auteur!.id)}>Voir les traces de ce compte</BoutonSecondaire>
        )}
      </div>
    </div>
  );
}

function ModaleExport({
  vue,
  filtres,
  bloque,
  onFermer,
}: {
  vue: VueJournal;
  filtres: FiltresJournal;
  bloque: boolean;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const [format, setFormat] = useState<FormatExportJournal>(vue.options.formats[0]?.code ?? 'XLSX');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const total = vue.pagination.total;
  const refus = bloque
    ? 'Indiquez une période complète avant d’exporter.'
    : total === 0
      ? 'Aucune trace ne correspond à ces filtres : il n’y a rien à exporter.'
      : total > vue.options.exportMax
        ? `${nombre(vue.options.exportMax)} traces au plus par export : resserrez la période ou les filtres.`
        : null;

  const exporter = async (): Promise<void> => {
    if (refus) return;
    setEnCours(true);
    setErreur(null);
    try {
      const resultat = await exporterJournal(filtres, format);
      toast(`Journal exporté — ${pluriel(resultat.lignes, 'trace')}`);
      onFermer();
    } catch (refusApi) {
      setErreur(messageErreur(refusApi, 'L’export n’a pas pu être produit.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale
      ouverte
      titre="Exporter le journal"
      sousTitre="La liste filtrée, toutes pages comprises"
      onFermer={onFermer}
      largeur="max-w-xl"
    >
      <div className="space-y-5 p-4 sm:p-6">
        <p className="text-sm text-codialis-navy">
          {pluriel(total, 'trace')} · {vue.periode.libelle}. L’export est lui-même tracé dans le journal, avec ses
          filtres : il fait sortir des noms d’apprenants et de comptes.
        </p>
        <fieldset>
          <legend className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">Format</legend>
          <div className="flex flex-wrap gap-2">
            {vue.options.formats.map((un) => {
              const actif = un.code === format;
              return (
                <label
                  key={un.code}
                  className={`flex min-h-[44px] min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                    actif
                      ? 'border-codialis-navy bg-codialis-navy text-white'
                      : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="format-export-journal"
                    className="sr-only"
                    checked={actif}
                    onChange={() => setFormat(un.code)}
                  />
                  <span className="min-w-0">{un.libelle}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
        {(refus ?? erreur) && <MessageErreur message={(refus ?? erreur)!} />}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal onClick={() => void exporter()} disabled={Boolean(refus) || enCours}>
            <span className="inline-flex items-center gap-2">
              <Download className="h-4 w-4" aria-hidden />
              {enCours ? 'Export…' : 'Exporter'}
            </span>
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}
